/**
 * 03/09/2026 (pedido do dono) — agrupamento dos avisos da importação de ponto /
 * sincronização por FUNCIONÁRIO. O mural solto (um texto amarelo por aviso) era
 * ilegível; a tela passa a mostrar um resumo e o detalhe agrupado em modal.
 *
 * Heurística (combinada com o dono): o aviso começa com o NOME EM MAIÚSCULAS da
 * pessoa seguido de ":" — ex.: "DANIEL PRADO RODRIGUES DE LIMA: atestado 22/07…".
 * O prefixo é casado com os nomes do cadastro normalizando caixa/acentos; sem
 * casamento, o aviso cai no grupo "Geral (sistema)".
 */
export interface GrupoAvisosPonto {
  /** Nome de exibição do grupo (nome do cadastro ou "Geral (sistema)"). */
  nome: string;
  avisos: string[];
}

/** Rótulo do grupo sem funcionário casado (sempre por último na ordenação). */
export const GRUPO_GERAL = "Geral (sistema)";

/** Normalização para o casamento: sem acentos, caixa alta, espaços colapsados. */
function normalizarNome(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

/** Prefixo "NOME:" no início do texto (o nome não passa de 80 chars nem contém ":"). */
const RE_PREFIXO = /^([^:]{2,80}):\s/;

/**
 * Agrupa os avisos por funcionário.
 * Ordem dos grupos: mais avisos primeiro; "Geral (sistema)" sempre por último.
 * Nomes mais longos casam primeiro (o prefixo mais específico vence).
 */
export function agruparAvisosPorFuncionario(avisos: string[], nomesFuncionarios: string[]): GrupoAvisosPonto[] {
  const nomes = nomesFuncionarios
    .map((n) => ({ exibicao: n, chave: normalizarNome(n) }))
    .filter((n) => n.chave.length > 0)
    .sort((a, b) => b.chave.length - a.chave.length);
  const grupos = new Map<string, string[]>();
  for (const aviso of avisos) {
    let grupo = GRUPO_GERAL;
    const m = aviso.match(RE_PREFIXO);
    if (m) {
      const prefixo = normalizarNome(m[1]!);
      const achado = nomes.find((n) => n.chave === prefixo);
      if (achado) grupo = achado.exibicao;
    }
    const lista = grupos.get(grupo) ?? [];
    lista.push(aviso);
    grupos.set(grupo, lista);
  }
  return [...grupos.entries()]
    .map(([nome, lista]) => ({ nome, avisos: lista }))
    .sort((a, b) => {
      if (a.nome === GRUPO_GERAL) return 1;
      if (b.nome === GRUPO_GERAL) return -1;
      return b.avisos.length - a.avisos.length || a.nome.localeCompare(b.nome, "pt-BR");
    });
}
